import { useState } from 'react'

import { Avatar, AvatarFallback } from '@/common/ui/avatar'
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/common/ui/collapsible'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@/common/ui/dropdown-menu'
import { Separator } from '@/common/ui/separator'
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarHeader,
	SidebarInput,
	SidebarInset,
	SidebarMenu,
	SidebarMenuBadge,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarProvider,
	SidebarRail,
	SidebarTrigger,
	useSidebar,
} from '@/common/ui/sidebar'
import {
	ChevronRight,
	ChevronsUpDown,
	Fan,
	LogOut,
	Wifi,
	WifiOff,
	Loader2,
	Search,
	X,
} from 'lucide-react'
import { Link, useLocation } from 'react-router-dom'
import { NavItem } from '@/common/types'
import { Command } from 'lucide-react'
import useLogout from '@/modules/auth/hooks/useLogout'
import useQueryProfile from '@/modules/auth/api/hooks/useQueryProfile'
import { useCallback, useMemo } from 'react'
import ThemeToggle from '@/common/components/ThemeToggle'
import { useSidebarBadges } from '@/common/hooks/useSidebarBadges'
import { cn } from '@/common/lib/utils'
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from '@/common/ui/tooltip'
import { useChatSseOptional } from '@/modules/chat/sse'
import { Button } from '@/common/ui/button'

const auto = {
	name: 'auto',
	logo: Fan,
}

type SidebarNavSearchProps = {
	searchQuery: string
	setSearchQuery: (value: string) => void
}

function SidebarNavSearch({
	searchQuery,
	setSearchQuery,
}: SidebarNavSearchProps) {
	const { setOpen } = useSidebar()

	return (
		<>
			<div className="relative group-data-[collapsible=icon]:hidden">
				<Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
				<SidebarInput
					placeholder="ค้นหาเมนู..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					className="pl-8 pr-8"
				/>
				{searchQuery && (
					<Button
						variant="ghost"
						size="icon"
						className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2"
						onClick={() => setSearchQuery('')}
					>
						<X className="h-3 w-3" />
					</Button>
				)}
			</div>
			<div className="hidden justify-center pb-2 group-data-[collapsible=icon]:flex">
				<SidebarMenu>
					<SidebarMenuItem>
						<SidebarMenuButton
							type="button"
							tooltip="ค้นหาเมนู"
							onClick={() => setOpen(true)}
						>
							<Search />
						</SidebarMenuButton>
					</SidebarMenuItem>
				</SidebarMenu>
			</div>
		</>
	)
}

export interface AppSidebarProps {
	prefixPath: string
	navItems: NavItem[]
	children: React.ReactNode
}

export default function AppSidebar({ navItems, children }: AppSidebarProps) {
	const { pathname } = useLocation()

	const { data: user } = useQueryProfile()

	const logout = useLogout()

	const { badges } = useSidebarBadges()

	const [searchQuery, setSearchQuery] = useState('')

	// SSE connection status (optional - only available when ChatSseProvider is present)
	const sseContext = useChatSseOptional()
	const sseStatus = sseContext?.status
	const connectionCount = sseContext?.connectionCount

	const getBadgeCount = useCallback(
		(badgeKey?: string) => {
			if (!badgeKey) return 0
			return badges[badgeKey] ?? 0
		},
		[badges],
	)

	const hasPermission = useCallback(
		(item: NavItem) => {
			if (!item.permissions) return true

			return (
				item.permissions.some((permission) =>
					user?.userGroup.readPermission.includes(permission as any),
				) ||
				item.permissions.some((permission) =>
					user?.userGroup.writePermission.includes(permission as any),
				)
			)
		},
		[user?.userGroup.readPermission, user?.userGroup.writePermission],
	)

	const filteredByPermission = useMemo(
		() => navItems.filter(hasPermission),
		[navItems, hasPermission],
	)

	const filteredNavItem = useMemo(() => {
		if (!searchQuery.trim()) {
			return filteredByPermission
		}

		const query = searchQuery.toLowerCase().trim()

		const synonymMap: Record<string, string[]> = {
			สมาชิก: ['ผู้เล่น'],
			ผู้เล่น: ['สมาชิก'],
		}

		const matchesWithSynonyms = (
			text: string,
			searchQuery: string,
		): boolean => {
			const lowerText = text.toLowerCase()

			if (lowerText.includes(searchQuery)) {
				return true
			}

			const synonyms = synonymMap[searchQuery]
			if (synonyms) {
				return synonyms.some((synonym) => lowerText.includes(synonym))
			}

			return false
		}

		return filteredByPermission
			.map((item) => {
				const matchesTitle = matchesWithSynonyms(item.title, query)

				if (item.items && item.items.length > 0) {
					const matchingSubItems = item.items.filter((subItem) => {
						const hasSubPermission = hasPermission(subItem)
						return hasSubPermission && matchesWithSynonyms(subItem.title, query)
					})

					if (matchingSubItems.length > 0) {
						return {
							...item,
							items: matchingSubItems,
						}
					}
				}

				return matchesTitle ? item : null
			})
			.filter((item): item is NavItem => item !== null)
	}, [filteredByPermission, searchQuery, hasPermission])

	return (
		<SidebarProvider>
			<Sidebar collapsible="icon">
				<SidebarHeader>
					<div className="flex items-center justify-between gap-2 py-2 text-sidebar-accent-foreground">
						<div className="flex gap-2">
							<div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
								<auto.logo className="size-4" />
							</div>
							<div className="grid flex-1 text-left text-sm leading-tight">
								<span className="truncate font-semibold">{auto.name}</span>
								<span className="truncate text-xs text-muted-foreground">
									v{import.meta.env.VITE_APP_VERSION || '0.0.0'}
								</span>
							</div>
						</div>
						<div className="group-data-[collapsible=icon]:hidden">
							<ThemeToggle />
						</div>
					</div>
					<SidebarNavSearch
						searchQuery={searchQuery}
						setSearchQuery={setSearchQuery}
					/>
				</SidebarHeader>
				<SidebarContent className="overflow-x-hidden">
					<SidebarGroup>
						<SidebarMenu>
							{filteredNavItem.length === 0 && searchQuery.trim() && (
								<div className="px-2 py-8 text-center text-sm text-muted-foreground">
									ไม่พบผลลัพธ์
								</div>
							)}
							{searchQuery.trim() ? (
								// Flat list view when searching
								<>
									{filteredNavItem.map((item) => {
										const isPermitted = hasPermission(item)
										const results: React.ReactNode[] = []

										// Add parent if it's a direct link (not just a group)
										if (isPermitted && item.url !== '#') {
											const icon = item.icon || <Command />
											const badgeCount = getBadgeCount(item.badgeKey)
											results.push(
												<SidebarMenuItem key={item.title}>
													<SidebarMenuButton
														asChild
														isActive={pathname === item.url}
													>
														<Link to={item.url}>
															{icon}
															<span>{item.title}</span>
															{item.badgeKey === 'chatUnreadCount' ? (
																<>
																	{badges.chatUnreadCount > 0 && (
																		<SidebarMenuBadge className="bg-destructive text-destructive-foreground">
																			{badges.chatUnreadCount > 99
																				? '99+'
																				: badges.chatUnreadCount}
																		</SidebarMenuBadge>
																	)}
																	{badges.chatNotifyCount > 0 && (
																		<SidebarMenuBadge className="bg-amber-500 text-white">
																			{badges.chatNotifyCount > 99
																				? '99+'
																				: badges.chatNotifyCount}
																		</SidebarMenuBadge>
																	)}
																</>
															) : (
																badgeCount > 0 && (
																	<SidebarMenuBadge className="bg-destructive text-destructive-foreground">
																		{badgeCount > 99 ? '99+' : badgeCount}
																	</SidebarMenuBadge>
																)
															)}
														</Link>
													</SidebarMenuButton>
												</SidebarMenuItem>,
											)
										}

										// Add all sub-items as flat list
										if (item.items && item.items.length > 0) {
											item.items.forEach((subItem) => {
												const isSubItemPermitted = hasPermission(subItem)
												if (isSubItemPermitted) {
													const subItemBadgeCount = getBadgeCount(
														subItem.badgeKey,
													)
													results.push(
														<SidebarMenuItem key={subItem.title}>
															<SidebarMenuButton
																asChild
																isActive={pathname === subItem.url}
															>
																<Link
																	to={subItem.url}
																	className="flex items-center gap-2"
																>
																	<span className="text-xs text-muted-foreground">
																		{item.title} →
																	</span>
																	<span>{subItem.title}</span>
																	{subItemBadgeCount > 0 && (
																		<SidebarMenuBadge className="bg-destructive text-destructive-foreground">
																			{subItemBadgeCount > 99
																				? '99+'
																				: subItemBadgeCount}
																		</SidebarMenuBadge>
																	)}
																</Link>
															</SidebarMenuButton>
														</SidebarMenuItem>,
													)
												}
											})
										}

										return results
									})}
								</>
							) : (
								// Hierarchical tree view when not searching
								<>
									{filteredNavItem.map((item) => {
										const icon = item.icon || <Command />
										const isPermitted = hasPermission(item)

										const defaultOpen =
											item.isActive ||
											pathname.includes(item.url) ||
											item.items?.some((subItem) =>
												pathname.includes(subItem.url),
											)

										const badgeCount = getBadgeCount(item.badgeKey)

										return item?.items && item?.items?.length > 0 ? (
											<Collapsible
												key={item.title}
												asChild
												defaultOpen={defaultOpen}
												className="group/collapsible"
											>
												<SidebarMenuItem>
													<CollapsibleTrigger asChild>
														{isPermitted && (
															<SidebarMenuButton
																tooltip={item.title}
																isActive={pathname === item.url}
															>
																{item.icon || null}
																<span>{item.title}</span>
																{badgeCount > 0 && (
																	<SidebarMenuBadge className="right-8 bg-destructive text-destructive-foreground group-data-[state=open]/collapsible:hidden">
																		{badgeCount > 99 ? '99+' : badgeCount}
																	</SidebarMenuBadge>
																)}
																<ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
															</SidebarMenuButton>
														)}
													</CollapsibleTrigger>
													<CollapsibleContent>
														<SidebarMenuSub>
															{item.items?.map((subItem) => {
																const isSubItemPermitted =
																	hasPermission(subItem)
																const subItemBadgeCount = getBadgeCount(
																	subItem.badgeKey,
																)

																return (
																	<SidebarMenuSubItem key={subItem.title}>
																		{isSubItemPermitted && (
																			<SidebarMenuSubButton
																				asChild
																				isActive={pathname === subItem.url}
																			>
																				<Link to={subItem.url}>
																					<span>{subItem.title}</span>
																					{subItemBadgeCount > 0 && (
																						<SidebarMenuBadge className="bg-destructive text-destructive-foreground">
																							{subItemBadgeCount > 99
																								? '99+'
																								: subItemBadgeCount}
																						</SidebarMenuBadge>
																					)}
																				</Link>
																			</SidebarMenuSubButton>
																		)}
																	</SidebarMenuSubItem>
																)
															})}
														</SidebarMenuSub>
													</CollapsibleContent>
												</SidebarMenuItem>
											</Collapsible>
										) : (
											<SidebarMenuItem key={item.title}>
												{isPermitted && (
													<SidebarMenuButton
														asChild
														tooltip={item.title}
														isActive={pathname === item.url}
													>
														<Link to={item.url}>
															{icon}
															<span>{item.title}</span>
														</Link>
													</SidebarMenuButton>
												)}
											</SidebarMenuItem>
										)
									})}
								</>
							)}
						</SidebarMenu>
					</SidebarGroup>
				</SidebarContent>
				<SidebarFooter>
					<SidebarMenu>
						<SidebarMenuItem>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<SidebarMenuButton
										size="lg"
										className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
									>
										<Avatar className="h-8 w-8 rounded-lg">
											<AvatarFallback className="rounded-lg">
												{user?.username?.slice(0, 2)?.toUpperCase() || 'CN'}
											</AvatarFallback>
										</Avatar>
										<div className="grid flex-1 text-left text-sm leading-tight">
											<span className="truncate font-semibold">
												{user?.username || ''}
											</span>
											<span className="truncate text-xs">
												{user?.userGroup.name || ''}
											</span>
										</div>
										<ChevronsUpDown className="ml-auto size-4" />
									</SidebarMenuButton>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
									side="bottom"
									align="end"
									sideOffset={4}
								>
									<DropdownMenuLabel className="p-0 font-normal">
										<div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
											<Avatar className="h-8 w-8 rounded-lg">
												<AvatarFallback className="rounded-lg">
													{user?.username?.slice(0, 2)?.toUpperCase() || 'CN'}
												</AvatarFallback>
											</Avatar>
											<div className="grid flex-1 text-left text-sm leading-tight">
												<span className="truncate font-semibold">
													{user?.username || ''}
												</span>
												<span className="truncate text-xs">
													{' '}
													{user?.userGroup.name || ''}
												</span>
											</div>
										</div>
									</DropdownMenuLabel>
									<DropdownMenuSeparator />

									{/* <DropdownMenuGroup>
										<DropdownMenuItem>
											<BadgeCheck />
											Account
										</DropdownMenuItem>
										<DropdownMenuItem>
											<CreditCard />
											Billing
										</DropdownMenuItem>
										<DropdownMenuItem>
											<Bell />
											Notifications
										</DropdownMenuItem>
									</DropdownMenuGroup> */}
									<DropdownMenuSeparator />
									<DropdownMenuItem onClick={logout}>
										<LogOut />
										ออกจากระบบ
									</DropdownMenuItem>
								</DropdownMenuContent>
							</DropdownMenu>
						</SidebarMenuItem>
					</SidebarMenu>
				</SidebarFooter>
				<SidebarRail />
			</Sidebar>
			<SidebarInset>
				<header className="flex h-16 shrink-0 items-center justify-between gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
					<div className="flex items-center gap-2 px-4">
						<SidebarTrigger className="-ml-1" />
						<Separator orientation="vertical" className="mr-2 h-4" />
					</div>
					{/* SSE Connection Status */}
					{sseStatus && (
						<div className="flex items-center gap-2 px-4">
							<TooltipProvider>
								<Tooltip>
									<TooltipTrigger asChild>
										<div
											className={cn(
												'flex h-8 items-center gap-1.5 rounded-md border px-2.5',
												sseStatus === 'connected' &&
													'border-green-200 bg-green-50 text-green-600 dark:border-green-800 dark:bg-green-950 dark:text-green-400',
												sseStatus === 'connecting' &&
													'border-yellow-200 bg-yellow-50 text-yellow-600 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-400',
												(sseStatus === 'disconnected' ||
													sseStatus === 'error') &&
													'border-red-200 bg-red-50 text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400',
											)}
										>
											{sseStatus === 'connected' ? (
												<Wifi className="h-4 w-4" />
											) : sseStatus === 'connecting' ? (
												<Loader2 className="h-4 w-4 animate-spin" />
											) : (
												<WifiOff className="h-4 w-4" />
											)}
											{sseStatus === 'connected' &&
												connectionCount !== null && (
													<span className="text-xs font-medium">
														{connectionCount}
													</span>
												)}
										</div>
									</TooltipTrigger>
									<TooltipContent>
										<p>
											{sseStatus === 'connected' &&
												(connectionCount !== null
													? `เชื่อมต่อแล้ว (${connectionCount} คนออนไลน์)`
													: 'เชื่อมต่อแล้ว')}
											{sseStatus === 'connecting' && 'กำลังเชื่อมต่อ...'}
											{sseStatus === 'disconnected' && 'ไม่ได้เชื่อมต่อ'}
											{sseStatus === 'error' && 'เกิดข้อผิดพลาด'}
										</p>
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</div>
					)}
				</header>
				{/* page main content */}
				{children}
			</SidebarInset>
		</SidebarProvider>
	)
}
